import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { isWeakPassword, mapApiError, mapPasswordViolations } from '@sentinel/auth-core';
import { registerSchema, type RegisterInput } from '../features/register/schema';
import { useRegister } from '../features/register/use-register';

interface ErrorBody {
  error?: string;
  message?: string;
}

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function getBody(e: unknown): ErrorBody | undefined {
  if (e && typeof e === 'object') {
    const maybe = e as { body?: ErrorBody; detail?: ErrorBody } & ErrorBody;
    return maybe.body ?? maybe.detail ?? maybe;
  }
  return undefined;
}

function RegisterPage() {
  const { t } = useTranslation('register');
  const navigate = useNavigate();
  const submit = useRegister();
  const [topError, setTopError] = useState<string | null>(null);

  const form = useForm<RegisterInput>({ resolver: zodResolver(registerSchema) });

  async function onSubmit(values: RegisterInput) {
    setTopError(null);
    try {
      await submit.mutateAsync({ email: values.email, password: values.password });
      // /welcome is added in Wave 2 Page 2; cast until then.
      await navigate({ to: '/welcome', search: { email: values.email } } as never);
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      if (status === 409) {
        // Keep the field-level email_taken hint (existing register namespace
        // string), but also keep the top-level alert keyed off a stable
        // code rather than raw body.message.
        setTopError(t('email_taken'));
        form.setError('email', { message: t('email_taken') });
        return;
      }
      if (isWeakPassword(body)) {
        const items = mapPasswordViolations(body);
        const message = items
          .map((it) => t(it.i18nKey, { ns: 'common', ...it.params }))
          .join(' ');
        form.setError('password', { type: 'policy', message });
        setTopError(null);
        return;
      }
      // Never surface raw body.message (tenant-controlled / phishing
      // vector). Map to a known i18n key in the common namespace.
      const errKey = mapApiError(body);
      setTopError(t(errKey, { ns: 'common' }));
    }
  }

  const confirmErrorRaw = form.formState.errors.confirm_password?.message;
  const confirmErrorMsg =
    confirmErrorRaw === 'passwords_must_match' ? t('passwords_must_match') : confirmErrorRaw;

  return (
    <AuthCard title={t('title')} subtitle={t('subtitle')}>
      {topError ? (
          <Alert type="error" role="alert">
            {topError}
          </Alert>
        ) : null}

        <form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'grid', gap: '0.75rem' }}>
          <FieldRow>
            <Label htmlFor="email">{t('email_label')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={form.formState.errors.email ? 'true' : undefined}
              {...form.register('email')}
            />
            <FieldError message={form.formState.errors.email?.message} />
          </FieldRow>

          <FieldRow>
            <Label htmlFor="password">{t('password_label')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.password ? 'true' : undefined}
              {...form.register('password')}
            />
            <FieldError message={form.formState.errors.password?.message} />
          </FieldRow>

          <FieldRow>
            <Label htmlFor="confirm_password">{t('confirm_password_label')}</Label>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              aria-invalid={form.formState.errors.confirm_password ? 'true' : undefined}
              {...form.register('confirm_password')}
            />
            <FieldError message={confirmErrorMsg} />
          </FieldRow>

          <Button type="submit" disabled={submit.isPending}>
            {t('submit')}
          </Button>
        </form>

      <p style={{ textAlign: 'center' }}>
        {t('have_account_prompt')} <Link to="/login">{t('sign_in_link')}</Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});
